import React, { useId } from "react";
import PropTypes from "prop-types";

import { useThemeA } from "../../internal/ThemeAContext";
import { FilterChip, FilterChipTrack } from "./FilterChip";

const nextSelection = ({ id, mode, selectedIds }) => {
  const selected = selectedIds.includes(id);
  if (mode === "single") return selected ? [] : [id];
  return selected ? selectedIds.filter((value) => value !== id) : [...selectedIds, id];
};

export function ChoiceChipGroup({ error = null, id, label, mode = "multiple", onSelectionChange, options, required = false, selectedIds = [], track = false }) {
  useThemeA("ChoiceChipGroup");
  // Dans un formulaire : astérisque sur la légende, erreur sous les puces.
  const generatedId = `ak-v2-choice-chips-${useId().replace(/:/g, "")}`;
  const groupId = id || generatedId;
  const errorId = error ? `${groupId}-error` : undefined;
  const chips = options.map((option) => (
    <FilterChip
      accent={option.accent}
      disabled={option.disabled}
      key={option.id}
      onClick={() => onSelectionChange(nextSelection({ id: option.id, mode, selectedIds }))}
      selected={selectedIds.includes(option.id)}
    >
      {option.label}
    </FilterChip>
  ));

  return (
    <fieldset aria-describedby={errorId} className="akv:m-0! akv:min-w-0 akv:border-0! akv:p-0!" data-ak-v2-component="choice-chip-group" id={groupId}>
      <legend className="akv:mb-2 akv:text-xs akv:font-bold akv:uppercase akv:tracking-[0.08em] akv:text-ak-muted">
        {label}
        {required ? <span className="akv:text-ak-critical" aria-hidden="true"> *</span> : null}
      </legend>
      {track ? <FilterChipTrack>{chips}</FilterChipTrack> : <div className="akv:flex akv:flex-wrap akv:gap-2">{chips}</div>}
      {error ? (
        <span className="akv:mt-2 akv:block akv:break-words akv:text-xs akv:font-semibold akv:leading-[1.35] akv:text-ak-critical" id={errorId} role="alert">{error}</span>
      ) : null}
    </fieldset>
  );
}

ChoiceChipGroup.propTypes = {
  error: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(["single", "multiple"]),
  onSelectionChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    // Couleur d'identité de l'option (ex. une couleur par type de note) : elle
    // remplit la pastille-coche et teinte le fond de survol conservé à la sélection.
    accent: PropTypes.string,
    disabled: PropTypes.bool,
    id: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
  })).isRequired,
  required: PropTypes.bool,
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  track: PropTypes.bool,
};
